import moment from 'moment'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { FILTER_DEFAULT_SKIP, FILTER_DEFAULT_TAKE } from '@apps-common/config/filter'
import { UnitTaskSort } from '@apps-common/config/unit-task'
import { IUnitTaskQueueSearchParamsModel } from '@apps-common/types/portal/unit-tasks'

export const getManagementUnitsFilterDefaultStartDateStr = () => '1990-01-01T00:00:00'
export const getManagementUnitsFilterDefaultEndDateStr = () => moment().add(1, 'day').toISOString()

export const DEFAULT_UNIT_TASK_QUEUE_FILTER_STORE_PROPS: IUnitTaskQueueFilterProps = {
  status: '',
  startDate: undefined,
  endDate: undefined,
  skip: FILTER_DEFAULT_SKIP,
  take: FILTER_DEFAULT_TAKE,
  orderBy: UnitTaskSort.dateAdded
}

export interface IUnitTaskQueueFilterProps {
  status: string
  startDate?: string
  endDate?: string
  skip: number
  take: number
  orderBy: string
}

export interface IUnitTaskQueueFilterState extends IUnitTaskQueueFilterProps {
  setStatus: (value: string) => void
  setStartDate: (value: string) => void
  setEndDate: (value: string) => void
  setSkip: (value: number) => void
  setTake: (value: number) => void
  setOrderBy: (orderBy: string) => void
  getSearchParams: () => IUnitTaskQueueSearchParamsModel
  getFilteredByKeywords: () => string[]
  clear: () => void
}

export const useUnitTaskQueueFilterStore = create<IUnitTaskQueueFilterState, [['zustand/persist', unknown]]>(
  persist(
    (set, get) => ({
      ...DEFAULT_UNIT_TASK_QUEUE_FILTER_STORE_PROPS,
      setStatus: (value: string) => {
        const { status } = get()

        if (status !== value) {
          set(() => ({ status: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setStartDate: (value: string) => {
        const { startDate } = get()

        if (startDate !== value) {
          set(() => ({ startDate: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setEndDate: (value: string) => {
        const { endDate } = get()

        if (endDate !== value) {
          set(() => ({ endDate: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setSkip: (value: number) => set(() => ({ skip: value })),
      setTake: (value: number) => set(() => ({ take: value })),
      setOrderBy: (value: string) => {
        const { orderBy } = get()

        if (orderBy !== value) {
          set(() => ({ orderBy: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      getSearchParams: () => {
        const { status, startDate, endDate, skip, take, orderBy } = get()

        return {
          status,
          startDate,
          endDate,
          skip,
          take,
          orderBy
        }
      },
      getFilteredByKeywords: () => {
        const { status, startDate, endDate, orderBy } = get()

        const keywords = []

        if (status && status !== '') {
          keywords.push('Status')
        }

        if (!!startDate || !!endDate) {
          keywords.push('Date')
        }

        if (orderBy && orderBy !== UnitTaskSort.dateAdded) {
          keywords.push('Order')
        }

        return keywords
      },
      clear: () => {
        set(() => DEFAULT_UNIT_TASK_QUEUE_FILTER_STORE_PROPS)
      }
    }),
    {
      name: 'unit-task-queue-filter-store'
    }
  )
)
